import React, { useEffect, useState, useRef, useImperativeHandle, useMemo, forwardRef, useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { unpack, pack } from 'msgpackr';
import stringify from '../utils/stableStringify';
import Calculator from '../utils/calculator';

import Box, { Item as BoxItem } from 'devextreme-react/box';
import AntCondition from '../utils/AntCondition';
import AntLocalization from '../utils/AntLocalization';
import AntPopup from './AntPOSPopup';
import AntBaseForm from './AntPOSForm';
import AntConfig from '../utils/AntConfig';
import AntAuth from '../utils/AntAuth';

import { AntGlobal, AntSession } from '../utils/AntContext';

export default forwardRef(function (props, ref) {
    const appGlobal = AntGlobal();
    // const session = AntSession();
    const location = useLocation();
    const navigate = useNavigate();

    if (props.API && !props.API.entityDisplay) {
        if (appGlobal && appGlobal.appConfig && appGlobal.appConfig.Services) {
            let found = appGlobal.appConfig.Services.find((s) => s.name === props.API.entityName);
            if (found) props.API.init({ entityDisplay: found.display });
        }
    }

    const mainTable = props.table ? props.table : props.API ? props.API.entityName : '';
    const mainEntityName = props.API && props.API.entityName ? props.API.entityName : '';
    const mainEntityDisplay = props.API && props.API.entityDisplay ? props.API.entityDisplay : props.entityDisplay ? props.entityDisplay : '';

    const mainConfig = useMemo(() => {
        let found = appGlobal.tableConfigs?.find((t) => t.tableName === mainTable);
        if (found) return found.config;

        let cnf = new AntConfig(appGlobal, mainTable);
        cnf.configFields(props.fields);

        if (appGlobal.tableConfigs instanceof Array === false) appGlobal.tableConfigs = [];

        appGlobal.tableConfigs.push({
            tableName: mainTable,
            config: cnf
        });
        return cnf;
    }, []);

    const popup = useRef();
    const header = useRef();

    const [mainData, setMainData] = useState({});
    const [mainState, setMainState] = useState(() => {
        let obj = {
            error: null,
            searchConditions: {}
        };

        return obj;
    });

    const [shouldRender, setShouldRender] = useState(1);

    const forceRender = useCallback(() => {
        setShouldRender((v) => v + 1);
    }, []);

    const initPopupSize = useCallback(() => {
        var minHeight;

        if (props.height instanceof Array) {
            for (let i = 0; i < props.height.length; i++) {
                const e = props.height[i];
                if (e && typeof e.when === 'object' && e.when !== null && e.value !== undefined) {
                    var isValid = AntCondition.isValid(mainData, {
                        when: e.when,
                        whenType: e.whenType
                    });
                    if (isValid) {
                        minHeight = parseFloat(e.value);
                        break;
                    }
                }
            }
        } else if (typeof props.height === 'number') minHeight = parseFloat(props.height);

        // if (!minHeight) minHeight = 500;

        var height = minHeight;

        var width = AntCondition.getValue(mainData, props.width);
        if (width === undefined) width = props.widthDefault;
        if (width === undefined) width = 900;

        return {
            width: width,
            height: height + 30
        };
    }, [props.height, props.width, props.widthDefault, mainData]);

    const onCustomMainState = useCallback(
        (data, changeData) => {
            data.popupSize = initPopupSize();
            return data;
        },
        [initPopupSize]
    );

    const forceRenderWithState = useCallback(
        (data) => {
            setMainState((v) => onCustomMainState(Object.assign(v, data), data));
            setShouldRender((v) => v + 1);
        },
        [onCustomMainState]
    );

    const onCustomMainData = useCallback((data, changeData, overide) => {
        if (overide) {
            if (data && typeof data === 'object') {
                let newObj = JSON.parse(stringify(changeData));

                let keys = Object.keys(data);
                for (let i = 0; i < keys.length; i++) {
                    const key = keys[i];
                    delete data[key];
                }

                return Object.assign(data, newObj);
            } else return changeData;
        }

        return Object.assign(data, changeData);
    }, []);

    const forceRenderWithData = useCallback(
        (data, overide) => {
            setMainData((v) => onCustomMainData(v, data, overide));
            setShouldRender((v) => v + 1);
        },
        [onCustomMainData]
    );

    const show = useCallback(
        async (objDefault) => {
            if (!popup.current) return;

            let obj = { ...objDefault };

            Object.keys(obj).forEach((k) => {
                if (obj[k] === undefined) delete obj[k];
            });

            var preparedObj = null;
            if (props.prepare !== false && props.API && props.API.postCreatePrepare) {
                preparedObj = await props.API.postCreatePrepare(obj, props.prepareConditions);
            }

            preparedObj = { ...preparedObj, ...props.defaultObj, ...obj };
            forceRenderWithState({ error: null, searchConditions: {} });

            if (props.onPrepared) await props.onPrepared(preparedObj);
            forceRenderWithData(preparedObj, true);

            popup.current.show();

            return;
        },
        [props.API, props.defaultObj, props.prepare, props.onPrepared, forceRenderWithState, forceRenderWithData]
    );

    const setError = useCallback(
        (content, errors) => {
            if (!content && (mainState.error === null || mainState.error === undefined)) return;

            if (errors == 'TypeError: Failed to fetch') errors = { vi: 'Không thể kết nối đến hệ thống !', en: 'Can not connect to system !', fr: 'Impossible de se connecter au système !', es: '¡No se puede conectar al sistema!', id: 'Tidak dapat terhubung ke sistem!', ja: 'システムに接続できません！', zh: '无法连接到系统！', ko: '시스템에 연결할 수 없습니다!', hi: 'सिस्टम से कनेक्ट नहीं हो सकता!', ar: 'لا يمكن الاتصال بالنظام!' };

            var values = errors === undefined || errors === null ? [] : errors instanceof Array ? errors : [errors];

            content = values.length === 1 ? values[0] : content;
            forceRenderWithState({
                error: content
                    ? {
                          text: String(content),
                          values: values
                      }
                    : null
            });
            if (props.onError) props.onError(content, errors);
        },
        [mainState, forceRenderWithState, props.onError]
    );

    const initHeader = useCallback(
        (formData) => {
            var isVisibleHeader = props.header && AntCondition.isVisible(formData, props.header);

            var headerColCount = undefined;
            if (props.header) {
                headerColCount = AntCondition.getValue(formData, props.header.colCount);
                if (headerColCount === undefined) headerColCount = props.header.colCountDefault ? props.header.colCountDefault : 1;
            }
            var headerItems = props.header && props.header.items instanceof Array ? props.header.items : [];
            headerItems = JSON.parse(stringify(headerItems));
            headerItems = headerItems.filter((item) => {
                return AntCondition.isVisible(formData, item);
            });

            return {
                headerItems: headerItems,
                headerVisible: isVisibleHeader
            };
        },
        [props.header]
    );

    const onFieldDataChanged = useCallback(
        async (e, newData) => {
            if (props.onFieldDataChanged) {
                var isValid = await props.onFieldDataChanged(e);
                if (!isValid) return;
            }

            forceRenderWithData(newData);
        },
        [props.onFieldDataChanged, forceRenderWithData]
    );

    const getValidData = useCallback(async () => {
        var isValid = true;

        if (header.current && header.current.validate) isValid = await header.current.validate();

        if (!isValid) return null;

        var obj = { ...mainData };

        if (obj && props.postFields instanceof Array) {
            Object.keys(obj).forEach((k) => {
                var found = props.postFields.find((field) => {
                    if (typeof field === 'object') return field.field === k;
                    else return field === k;
                });

                if (!found) obj[k] = undefined;
                else {
                    props.postFields.forEach((field) => {
                        if (typeof field === 'object') {
                            var rawField = field.field;
                            var postField = field.alias;
                            if (rawField === k) {
                                obj[postField] = obj[rawField];
                                obj[rawField] = undefined;
                            }
                        }
                    });
                }
            });
        }

        Object.keys(obj).forEach((k) => {
            if (obj[k] === undefined) delete obj[k];
        });

        return obj;
    }, [props.postFields, mainData]);

    const save = useCallback(async () => {
        setError(null);

        var obj = await getValidData();
        if (!obj || Object.keys(obj).length <= 0) return false;

        // var keys = Object.keys(obj);
        // for (let i = 0; i < keys.length; i++) {
        //     const key = keys[i];
        //     const ar = obj[key];
        //     if (ar instanceof Array && ar.length <= 0) {
        //         setError('Dữ liệu không được trống !');
        //         return false;
        //     }
        // }

        if (props.onCreating) obj = props.onCreating(mainData, obj);

        var errors = [];
        var errorContent = null;
        var result;

        if (props.actionName) {
            result = await props.API.postActionWithData(props.actionName, obj);

            if (!result || result.error) {
                errors = mainConfig.translateErrors(result);
                errorContent = errors.length > 0 ? errors[0] : null;
            }
        } else {
            result = await props.API.postCreateValidation(obj);
            errors = mainConfig.translateErrors(result);
            errorContent = errors.length > 0 ? errors[0] : null;

            if (errors.length <= 0) {
                result = await props.API.postCreate(obj);
                if (!result || result.error || !result.data || !result.data.inserted) {
                    errors = mainConfig.translateErrors(result);
                    errorContent = errors.length > 0 ? errors[0] : null;
                }
            }
        }

        if (errors.length > 0) {
            if (!errorContent) errorContent = AntLocalization.translateText({ vi: 'Không thể truy xuất dữ liệu !', en: 'Connection timeout !', fr: 'Délai de connexion dépassé !', es: '¡Tiempo de conexión agotado!', id: 'Waktu koneksi habis!', ja: '接続がタイムアウトしました！', zh: '连接超时！', ko: '연결 시간 초과!', hi: 'कनेक्शन टाइमआउट!', ar: 'انتهت مهلة الاتصال!' });
            setError(errorContent, errors);
            if (props.onError) props.onError(errorContent, errors);
            return false;
        } else if (result && result.data) {
            if (props.onCreated)
                setTimeout(
                    async (data) => {
                        props.onCreated(data);
                    },
                    1000,
                    result.data
                );

            if (props.navigateLink && result.data) {
                var value;
                var link;

                if (props.navigateLink instanceof Array) {
                    props.navigateLink.forEach((l) => {
                        if (l && l.link) {
                            if (l.when && typeof l.when === 'object') {
                                var isValid = AntCondition.isValid(mainData, {
                                    when: l.when,
                                    whenType: l.whenType
                                });
                                if (!isValid) return;
                            }
                            value = l.field ? result.data[l.field] : result.data.code;
                            link = String(l.link);
                        }
                    });
                } else {
                    value = props.navigateField ? result.data[props.navigateField] : result.data.code;
                    link = String(props.navigateLink);
                }
                if (link && value !== undefined && value !== null) {
                    if (!link.startsWith('/')) link = '/' + link;
                    if (!link.endsWith('/')) link = link + '/';
                    link = link + value;
                    navigate(link);
                }
            }
        }
        return true;
    }, [
        setError,
        getValidData,
        props.onCreating,
        mainData,
        props.actionName,
        props.API,
        mainConfig,
        props.onError,
        props.onCreated,
        props.navigateLink,
        props.navigateField
    ]);

    const onAction = useCallback(
        async (button) => {
            var close = false;

            if (button && button.id == 'btnSave') close = await save();

            if (!close && props.onAction) close = await props.onAction(button);

            return close;
        },
        [props.onAction, save]
    );

    const mainHeader = useMemo(() => initHeader(mainData), [mainData, initHeader]);

    useImperativeHandle(ref, () => ({
        show: show
    }));

    // useEffect(() => {
    //     return () => {
    //         delete popup.current;
    //         delete form.current;

    //         popup.current = null;
    //         form.current = null;
    //     };
    // }, []);

    return (
        <React.Fragment>
            {shouldRender && (
                <AntPopup
                    ref={popup}
                    isGray={true}
                    shouldRender={shouldRender + (props.shouldRender ? props.shouldRender : 1)}
                    width={mainState.popupSize ? mainState.popupSize.width : undefined}
                    height={mainState.popupSize ? mainState.popupSize.height : undefined}
                    autoHeight={true}
                    autoWidth={true}
                    title={props.title ? AntLocalization.translateText(props.title) : `Tạo mới ${mainEntityDisplay}`}
                    onAction={onAction}
                    name={`ant-voucher-creation-${props.API.entityName}`}
                    errors={mainState.error ? mainState.error.values : undefined}
                    errorText={mainState.error ? mainState.error.text : undefined}
                    separateErrorLine={props.separateErrorLine}
                    hideOnOutsideClick={props.hideOnOutsideClick !== undefined ? props.hideOnOutsideClick : true}
                    widgets={[
                        {
                            id: 'btnSave',
                            type: 'button',
                            icon: 'save',
                            text: AntLocalization.translateText({ vi: 'Tạo', en: 'Create', fr: 'Créer', es: 'Crear', id: 'Buat', ja: '作成', zh: '创建', ko: '생성', hi: 'बनाएं', ar: 'إنشاء' }),
                            waiting_text: AntLocalization.translateText({ vi: 'Chờ xử lý', en: 'Processing', fr: 'Traitement', es: 'Procesando', id: 'Memproses', ja: '処理中', zh: '处理中', ko: '처리 중', hi: 'प्रसंस्करण', ar: 'جاري المعالجة' }),
                            width: 130
                        }
                    ]}
                >
                    {/* {mainState.error && (
                        <BoxItem baseSize={40}>
                            <AntErrorPanel
                            errors={mainState.error.values}
                                id={`ant-voucher-creation-error-${props.API.entityName}`}
                            text={mainState.error.text}
                            />
                        </BoxItem>
                    )} */}

                    {mainHeader && mainHeader.headerVisible && (
                        <AntBaseForm
                            ref={header}
                            type={'Create'}
                            formData={mainData}
                            shouldRender={shouldRender + (props.shouldRender ? props.shouldRender : 1)}
                            table={props.header ? props.header.table : props.table}
                            default={props.header ? props.header.default : undefined}
                            API={props.header && props.header.API ? props.header.API : props.API}
                            parentData={props.parentData}
                            parentFK={props.parentFK}
                            labelLocation={props.header ? props.header.labelLocation : undefined}
                            alignItemLabelsInAllGroups={props.header ? props.header.alignItemLabelsInAllGroups : undefined}
                            colCount={props.header ? props.header.colCount : 1}
                            colCountDefault={props.header ? props.header.colCountDefault : undefined}
                            prepare={false}
                            disabled={props.header && props.header.disabled !== undefined ? props.header.disabled : undefined}
                            fields={props.header ? props.header.fields : []}
                            items={props.header ? props.header.items : []}
                            postFields={props.header ? props.header.postFields : undefined}
                            onFieldDataChanged={onFieldDataChanged}
                            onError={setError}
                        />
                    )}
                </AntPopup>
            )}
        </React.Fragment>
    );
});
